<script lang="ts" setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { ChevronDownIcon, DocumentDownloadIcon } from '@heroicons/vue/solid';
import { ExportFormat } from '@/utils/spreadsheet';

defineProps({
    compact: {
        type: Boolean,
        default: false,
    },
    label: {
        type: String,
        default: 'Download',
    },
});

const emit = defineEmits<{
    (event: 'select', format: ExportFormat): void;
}>();

const selectFormat = (format: ExportFormat) => {
    emit('select', format);
};
</script>

<template>
    <Menu as="div" class="relative inline-block text-left">
        <MenuButton
            class="hover:text-primary focus:ring-primary inline-flex items-center justify-center bg-white font-medium text-gray-700 shadow ring-1 ring-gray-300 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-opacity-50"
            :class="compact ? 'h-9 w-9 rounded-full' : 'rounded px-3 py-2 text-sm'"
            v-tippy="compact ? label : undefined"
        >
            <DocumentDownloadIcon
                class="h-5 w-5 text-gray-500"
                :class="compact ? '' : '-ml-1 mr-2'"
                aria-hidden="true"
            />
            <span :class="compact ? 'sr-only' : ''">{{ label }}</span>
            <ChevronDownIcon
                v-if="!compact"
                class="ml-2 h-4 w-4 text-gray-400"
                aria-hidden="true"
            />
        </MenuButton>

        <MenuItems
            class="absolute right-0 z-20 mt-2 w-64 origin-top-right overflow-hidden rounded bg-white shadow-xl ring-1 ring-gray-300 focus:outline-none"
        >
            <div class="border-b border-gray-100 bg-gray-50 px-4 py-3">
                <p class="text-sm font-semibold text-gray-800">Escolha o formato</p>
                <p class="text-xs text-gray-500">Baixe os dados em planilha ou texto.</p>
            </div>

            <div class="p-2">
                <MenuItem v-slot="{ active }">
                    <button
                        type="button"
                        class="flex w-full items-center rounded px-3 py-2 text-left transition"
                        :class="active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'"
                        @click="selectFormat('xlsx')"
                    >
                        <span
                            class="mr-3 inline-flex h-9 w-12 items-center justify-center rounded bg-emerald-100 text-xs font-bold text-emerald-700"
                        >
                            XLSX
                        </span>
                        <span class="min-w-0">
                            <span class="block text-sm font-semibold">Excel (.xlsx)</span>
                            <span class="block text-xs text-gray-500"
                                >Planilha nativa para Excel</span
                            >
                        </span>
                    </button>
                </MenuItem>

                <MenuItem v-slot="{ active }">
                    <button
                        type="button"
                        class="mt-1 flex w-full items-center rounded px-3 py-2 text-left transition"
                        :class="active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'"
                        @click="selectFormat('csv')"
                    >
                        <span
                            class="mr-3 inline-flex h-9 w-12 items-center justify-center rounded bg-sky-100 text-xs font-bold text-sky-700"
                        >
                            CSV
                        </span>
                        <span class="min-w-0">
                            <span class="block text-sm font-semibold">CSV</span>
                            <span class="block text-xs text-gray-500"
                                >Arquivo leve separado por colunas</span
                            >
                        </span>
                    </button>
                </MenuItem>
            </div>
        </MenuItems>
    </Menu>
</template>
